import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Wand2, Loader2, Layers, Trophy, Lock, Sparkles, Play, ChevronDown, ChevronRight,
  Crown, Star, Gift, Trash2, Gamepad2, DollarSign, ShoppingCart,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Spinner } from "@/components/Bits";
import GameCanvas from "@/components/GameCanvas";
import GrowthAssistant from "@/components/GrowthAssistant";
import StorePageBuilder from "@/components/StorePageBuilder";
import GamePassBuilder from "@/components/GamePassBuilder";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const TYPE_ICON = { boss: Crown, bonus: Star, secret: Lock, normal: null };
const ACCESS_VARIANT = { free: "default", premium: "secondary", locked: "destructive", unlockable: "outline" };
const PRESETS = [10, 50, 100, 500, 1000];

export default function LevelStudio() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [meta, setMeta] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [worldLevels, setWorldLevels] = useState({});
  const [generating, setGenerating] = useState(false);
  const [play, setPlay] = useState(null);
  const [params, setParams] = useState({ count: 100, levels_per_world: 20, free_count: 15, boss_every: 25, curve: "gentle", theme: "" });

  const load = useCallback(async () => {
    try {
      const [g, l] = await Promise.all([api.get(`/games/${gameId}`), api.get(`/games/${gameId}/levels`)]);
      setGame(g.data); setMeta(l.data.meta); setWorldLevels({}); setExpanded({});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [gameId]);
  useEffect(() => { load(); }, [load]);

  const toggleWorld = async (wid) => {
    if (expanded[wid]) { setExpanded((e) => ({ ...e, [wid]: false })); return; }
    setExpanded((e) => ({ ...e, [wid]: true }));
    if (!worldLevels[wid]) {
      try {
        const { data } = await api.get(`/games/${gameId}/levels?world=${wid}`);
        setWorldLevels((m) => ({ ...m, [wid]: data.levels }));
      } catch (e) {
        setExpanded((ex) => ({ ...ex, [wid]: false }));
        toast.error(formatApiError(e.response?.data?.detail));
      }
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/games/${gameId}/levels/generate`, params);
      await load();
      toast.success(`Generated ${data.count} levels across ${data.worlds.length} worlds! 🎉`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const setAccess = async (wid, level, access) => {
    try {
      await api.patch(`/games/${gameId}/levels/${level.id}`, { access });
      setWorldLevels((m) => ({ ...m, [wid]: m[wid].map((l) => (l.id === level.id ? { ...l, access } : l)) }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openPlay = async (level) => {
    try {
      const { data } = await api.get(`/games/${gameId}/levels/${level.id}/play`);
      setPlay({ level: data.level, config: data.config });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const clearAll = async () => {
    if (!window.confirm("Remove all levels for this game?")) return;
    try { await api.post(`/games/${gameId}/levels/clear`); toast.success("Levels cleared"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!meta) return <Spinner />;
  const hasLevels = (meta.count || 0) > 0;
  const premiumCount = (meta.count || 0) - (meta.free_count || 0);

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="level-studio-page">
      <button onClick={() => navigate(`/app/build/${gameId}`)} className="flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-4" data-testid="back-to-builder">
        <ArrowLeft className="w-4 h-4" /> Back to builder
      </button>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1f6feb] to-[#3b9eff] grid place-items-center"><Layers className="w-6 h-6 text-white" /></div>
        <div>
          <h1 className="font-display text-2xl font-bold leading-tight">Level Studio</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{game?.title} · build up to 1000 AI-generated levels</p>
        </div>
      </div>

      {/* Generator */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-6" data-testid="level-generator">
        <h2 className="font-bold mb-3 flex items-center gap-2"><Wand2 className="w-4 h-4 text-[hsl(var(--primary))]" /> AI Level Generator</h2>
        <Input value={params.theme} onChange={(e) => setParams({ ...params, theme: e.target.value })}
          placeholder="Describe your campaign… e.g. a brave fox exploring magical floating islands" className="mb-3" data-testid="gen-theme" />
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="text-xs text-[hsl(var(--muted-foreground))] self-center mr-1">Total levels:</span>
          {PRESETS.map((p) => (
            <Button key={p} size="sm" variant={params.count === p ? "default" : "outline"} className="rounded-full"
              onClick={() => setParams({ ...params, count: p })} data-testid={`preset-${p}`}>{p}</Button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Field label="Levels / world"><Input type="number" min="5" max="50" value={params.levels_per_world} onChange={(e) => setParams({ ...params, levels_per_world: +e.target.value })} data-testid="gen-lpw" /></Field>
          <Field label="Free levels"><Input type="number" min="0" value={params.free_count} onChange={(e) => setParams({ ...params, free_count: +e.target.value })} data-testid="gen-free" /></Field>
          <Field label="Boss every"><Input type="number" min="0" value={params.boss_every} onChange={(e) => setParams({ ...params, boss_every: +e.target.value })} data-testid="gen-boss" /></Field>
          <Field label="Difficulty curve">
            <Select value={params.curve} onValueChange={(v) => setParams({ ...params, curve: v })}>
              <SelectTrigger data-testid="gen-curve"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gentle">Gentle</SelectItem>
                <SelectItem value="linear">Linear</SelectItem>
                <SelectItem value="steep">Steep</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={generate} disabled={generating} className="rounded-full" data-testid="generate-levels-btn">
            {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating {params.count} levels…</> : <><Sparkles className="w-4 h-4" /> Generate {params.count} levels</>}
          </Button>
          {hasLevels && <Button variant="ghost" onClick={clearAll} className="rounded-full text-[hsl(var(--destructive))]" data-testid="clear-levels-btn"><Trash2 className="w-4 h-4" /> Clear all</Button>}
        </div>
        {generating && params.count >= 500 && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">Big campaigns take ~20s — designing worlds and balancing difficulty…</p>}
      </div>

      {!hasLevels ? (
        <div className="text-center py-16 text-[hsl(var(--muted-foreground))]" data-testid="no-levels">
          <Gamepad2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No levels yet. Describe your idea above and generate your first campaign!</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-5" data-testid="level-stats">
            <Stat icon={Layers} label="Levels" value={meta.count} />
            <Stat icon={Sparkles} label="Free" value={meta.free_count} accent="#22c55e" />
            <Stat icon={Lock} label="Premium" value={premiumCount} accent="#f59e0b" />
            <Stat icon={Trophy} label="Worlds" value={meta.worlds.length} accent="#a855f7" />
            <Stat icon={Crown} label="Boss every" value={meta.boss_every || "—"} accent="#ef4444" />
          </div>

          <MonetizationPanel gameId={gameId} premiumCount={premiumCount} published={game?.status === "published"} />
          <GamePassBuilder gameId={gameId} />
          <GrowthAssistant gameId={gameId} />
          <StorePageBuilder gameId={gameId} />

          <div className="space-y-3" data-testid="worlds-list">
            {meta.worlds.map((w) => (
              <div key={w.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden" data-testid={`world-${w.index}`}>
                <button onClick={() => toggleWorld(w.id)} className="w-full flex items-center gap-3 p-4 hover:bg-[hsl(var(--muted))]/30 transition-colors text-left" data-testid={`world-toggle-${w.index}`}>
                  {expanded[w.id] ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                  <div className="flex gap-1 shrink-0">
                    {["bg", "ground", "accent"].map((c) => <span key={c} className="w-3 h-6 rounded" style={{ background: w.palette?.[c] }} />)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">World {w.index}: {w.name}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{w.theme}</div>
                  </div>
                  <Badge variant={w.access === "free" ? "default" : w.access === "premium" ? "secondary" : "outline"} className="capitalize shrink-0">{w.access}</Badge>
                  <span className="text-xs text-[hsl(var(--muted-foreground))] shrink-0 tabular-nums">{w.level_count} lvl</span>
                </button>
                {expanded[w.id] && (
                  <div className="px-4 pb-4">
                    {!worldLevels[w.id] ? <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[hsl(var(--primary))]" /></div> : (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {worldLevels[w.id].map((l) => {
                          const Icon = TYPE_ICON[l.type];
                          return (
                            <div key={l.id} className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] p-2.5" data-testid={`level-${l.index}`}>
                              <span className="text-xs font-mono text-[hsl(var(--muted-foreground))] w-8 shrink-0">#{l.index}</span>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate flex items-center gap-1">{Icon && <Icon className="w-3 h-3 text-amber-400" />}{l.name}</div>
                                <div className="text-[10px] text-[hsl(var(--muted-foreground))]">Difficulty {l.difficulty}/10</div>
                              </div>
                              <Select value={l.access} onValueChange={(v) => setAccess(w.id, l, v)}>
                                <SelectTrigger className="h-7 w-[88px] text-[11px] shrink-0" data-testid={`access-${l.index}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="free">Free</SelectItem>
                                  <SelectItem value="premium">Premium</SelectItem>
                                  <SelectItem value="locked">Locked</SelectItem>
                                  <SelectItem value="unlockable">Secret</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => openPlay(l)} data-testid={`play-${l.index}`}><Play className="w-3.5 h-3.5" /></Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!play} onOpenChange={(o) => !o && setPlay(null)}>
        <DialogContent className="max-w-3xl" data-testid="play-level-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-4 h-4 text-[hsl(var(--primary))]" /> {play?.level?.name}
              <Badge variant={ACCESS_VARIANT[play?.level?.access] || "secondary"} className="capitalize text-[10px]">{play?.level?.access}</Badge>
              <Badge variant="outline" className="text-[10px]">Difficulty {play?.level?.difficulty}/10</Badge>
            </DialogTitle>
            <DialogDescription className="sr-only">Play this generated level</DialogDescription>
          </DialogHeader>
          {play && <GameCanvas config={play.config} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">{label}</label><div className="mt-1">{children}</div></div>
);

function MonetizationPanel({ gameId, premiumCount, published }) {
  const [mon, setMon] = useState(null);
  const [saving, setSaving] = useState(false);
  const [price, setPrice] = useState("4.99");
  const [title, setTitle] = useState("Premium Level Pack");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/games/${gameId}/monetization`);
        setMon(data); setPrice(String(data.price || 4.99)); setTitle(data.pack_title || "Premium Level Pack");
      } catch (e) { /* ignore */ }
    })();
  }, [gameId]);
  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/games/${gameId}/monetization`, patch);
      setMon(data); setPrice(String(data.price)); setTitle(data.pack_title);
      if (patch.enabled === true) toast.success(data.status === "approved" ? "Premium pack is live! 💰" : "Sent for approval — an admin/parent will review it.");
      else if (patch.enabled === false) toast.success("Sales turned off.");
      else toast.success("Saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!mon) return null;
  const sb = { approved: ["Live 💰", "default"], pending: ["Pending approval", "secondary"], rejected: ["Rejected", "destructive"], draft: ["Off", "outline"] }[mon.status] || ["Off", "outline"];
  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 mb-6" data-testid="monetization-panel">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h2 className="font-bold flex items-center gap-2"><DollarSign className="w-4 h-4 text-amber-500" /> Monetization — Premium Level Pack</h2>
        <Badge variant={sb[1]} data-testid="mon-status">{sb[0]}</Badge>
      </div>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Sell access to your {premiumCount} premium levels. Players pay once to unlock them all. <span className="font-semibold">Stripe TEST mode — no real charges.</span></p>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <Field label="Pack name"><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="mon-title" /></Field>
        <Field label="Price (USD)"><Input type="number" step="0.01" min="0.99" max="49.99" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="mon-price" /></Field>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" disabled={saving} onClick={() => save({ price: parseFloat(price) || 4.99, pack_title: title })} data-testid="mon-save-btn">Save details</Button>
        {mon.enabled ? (
          <Button variant="ghost" disabled={saving} onClick={() => save({ enabled: false })} className="text-[hsl(var(--destructive))]" data-testid="mon-disable-btn">Turn off sales</Button>
        ) : (
          <Button disabled={saving || premiumCount === 0 || !published} onClick={() => save({ enabled: true, price: parseFloat(price) || 4.99, pack_title: title })} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="mon-enable-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />} Enable sales
          </Button>
        )}
      </div>
      {premiumCount === 0 && <p className="text-xs text-amber-600 mt-2">Mark some levels as Premium below to sell them.</p>}
      {!published && <p className="text-xs text-amber-600 mt-2">Publish your game from the builder before enabling sales.</p>}
      {mon.status === "pending" && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">Waiting for an admin/parent to approve before buyers can purchase.</p>}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, accent = "#3b9eff" }) => (
  <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
    <Icon className="w-4 h-4 mb-1" style={{ color: accent }} />
    <div className="text-xl font-bold tabular-nums">{value}</div>
    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{label}</div>
  </div>
);
